import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, ArrowRight, Clock, AlertCircle, BadgeCheck, Wallet, Activity, CheckCircle2 } from "lucide-react";
import { fetchDashboard, setAvailability } from "@lawyer/services/lawyerApi";
import { LAWYER_BASE } from "@lawyer/constants/route";
import LawyerPulseCard from "@lawyer/components/LawyerPulseCard";

const fmt = (n) => (n || 0).toLocaleString("en-IN");

function StatCard({ label, value, hint, accent = "navy", testid }) {
  const accents = {
    navy: "bg-[#0A2540] text-white",
    gold: "bg-[#FBF4DF] text-[#0A2540] border border-[#F1E3B5]",
    emerald: "bg-emerald-50 text-emerald-900 border border-emerald-200",
    rose: "bg-rose-50 text-rose-900 border border-rose-200",
  }[accent];
  return (
    <div className={`rounded-2xl p-5 ${accents}`} data-testid={testid}>
      <p className="text-[11px] uppercase tracking-widest opacity-70 font-bold">{label}</p>
      <p className="mt-2 text-3xl font-extrabold" style={{ fontFamily: "Poppins" }}>{value}</p>
      {hint && <p className="mt-1 text-xs opacity-80">{hint}</p>}
    </div>
  );
}

const STATUS_PILL = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-800" },
  confirmed: { label: "Confirmed", cls: "bg-blue-100 text-blue-800" },
  accepted: { label: "Accepted", cls: "bg-blue-100 text-blue-800" },
  live: { label: "Live", cls: "bg-emerald-100 text-emerald-800" },
};

export default function LawyerDashboard({ data, onReload }) {
  // `data` is provided by LawyerApp shell so the sidebar status reflects same fetch.
  const [busy, setBusy] = useState(false);
  if (!data) return <div className="text-slate-500" data-testid="lawyer-dashboard-loading">Loading…</div>;

  const { lawyer, counts, today_agenda, performance, earnings } = data;

  const fmtMin = (s) => {
    if (s == null) return "—";
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    return `${(s / 3600).toFixed(1)}h`;
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6" data-testid="lawyer-dashboard-header">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            Hi, {lawyer.name?.replace(/^Adv\.\s*/, "").split(" ")[0] || "Counsel"}
          </h1>
          <p className="mt-1 text-sm text-slate-500 inline-flex items-center gap-2">
            {lawyer.verified
              ? <span className="inline-flex items-center gap-1 text-emerald-700"><BadgeCheck size={14} /> Verified</span>
              : <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle size={14} /> Verification pending</span>}
            <span>·</span>
            <span>{lawyer.specialization} · {lawyer.city}</span>
          </p>
        </div>
        {!lawyer.verified && (
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-3 py-2" data-testid="lawyer-verification-banner">
            Your profile is awaiting admin verification — clients won't see you in search yet.
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard testid="stat-upcoming" label="Upcoming" value={fmt(counts.upcoming)} hint={`${counts.live} live now`} accent="navy" />
        <StatCard testid="stat-completed" label="Completed" value={fmt(counts.completed)} hint={`${performance.completion_rate_pct}% completion rate`} accent="emerald" />
        <StatCard testid="stat-earnings" label="Net earnings" value={`₹${fmt(earnings.net_total)}`} hint={`After ${earnings.commission_pct}% commission`} accent="gold" />
        <StatCard testid="stat-missed" label="Missed/Cancelled" value={fmt(performance.missed_count)} hint={`Avg response: ${fmtMin(performance.avg_response_seconds)}`} accent="rose" />
      </div>

      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3 flex items-center justify-between">
        <span><Calendar size={12} className="inline mr-1" /> Today's agenda</span>
        <Link to={`${LAWYER_BASE}/consultations?bucket=upcoming`} className="text-[11px] font-bold text-[#0A2540] hover:underline" data-testid="see-all-consultations">
          See all <ArrowRight size={10} className="inline" />
        </Link>
      </h2>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="today-agenda">
        {today_agenda.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">
            <Activity size={28} className="mx-auto text-slate-300 mb-2" />
            No consultations scheduled for today. <span className="text-[#0A2540] font-bold">{lawyer.available ? "Stay online for instant bookings." : "Go online to accept instant bookings."}</span>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {today_agenda.map((c) => {
              const pill = STATUS_PILL[c.status] || { label: c.status, cls: "bg-slate-100 text-slate-700" };
              return (
                <li key={c.id} className="flex items-center gap-4 p-4 hover:bg-slate-50">
                  <div className="text-center min-w-[60px]">
                    <p className="text-xs text-slate-400">{(c.slot_time || "—")}</p>
                    <p className="text-[10px] uppercase font-bold text-slate-400 mt-0.5">{c.consultation_type}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#0A2540] text-sm">{c.user_name || "Client"}</p>
                    <p className="text-xs text-slate-500 truncate">₹{fmt(c.amount)} · ID {(c.id || "").slice(0, 8)}</p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${pill.cls}`}>{pill.label}</span>
                  <Link to={`${LAWYER_BASE}/consultations`} className="text-xs font-bold text-[#0A2540] hover:underline" data-testid={`agenda-open-${c.id}`}>
                    Open
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-3">
        <LawyerPulseCard lawyer={lawyer} onChanged={() => onReload?.()} />
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Performance</p>
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-600" /><div><p className="text-[10px] text-slate-400 uppercase">Completion rate</p><p className="font-bold text-[#0A2540]">{performance.completion_rate_pct}%</p></div></div>
            <div className="flex items-center gap-2"><Clock size={14} className="text-blue-600" /><div><p className="text-[10px] text-slate-400 uppercase">Avg response</p><p className="font-bold text-[#0A2540]">{fmtMin(performance.avg_response_seconds)}</p></div></div>
            <div className="flex items-center gap-2"><AlertCircle size={14} className="text-rose-600" /><div><p className="text-[10px] text-slate-400 uppercase">Missed/Cancel</p><p className="font-bold text-[#0A2540]">{fmt(performance.missed_count)}</p></div></div>
            <div className="flex items-center gap-2"><Activity size={14} className="text-amber-600" /><div><p className="text-[10px] text-slate-400 uppercase">Pipeline</p><p className="font-bold text-[#0A2540]">{fmt(counts.upcoming + counts.live)}</p></div></div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Earnings glance</p>
          <p className="mt-2 text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            ₹{fmt(earnings.net_total)}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            From ₹{fmt(earnings.gross_paid)} gross · platform takes {earnings.commission_pct}%
          </p>
          <Link to={`${LAWYER_BASE}/earnings`} className="mt-3 inline-flex items-center text-xs font-bold text-[#0A2540] hover:underline" data-testid="see-earnings">
            <Wallet size={12} className="mr-1" /> See full breakdown <ArrowRight size={10} className="ml-1" />
          </Link>
        </div>
      </div>
    </>
  );
}
